import React, {Dispatch, ForwardedRef, forwardRef, SetStateAction} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/components/trans';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {renderPageNumbers, StyledPageSelectorButton} from '../../table/tablePagination/tablePageSelector';
import {TablePageSizeDropdownBox} from '../../table/tablePagination/tablePageSizeDropdown';

/*
 * Types.
 */

interface PaginatedTableFooterProps {
  total: number;
  pageSize: number;
  pageSizes: ReadonlyArray<number>;
  setPageSize: Dispatch<SetStateAction<number>>;
  pageIndex: number;
  setPageIndex: Dispatch<SetStateAction<number>>;
}

/*
 * Styles.
 */

const StyledPaginatedTableFooter = styled.div`
  margin-top: 20px;
  width: 100%;
  grid-auto-flow: column;
  align-items: center;
  display: grid;
  justify-content: space-between;
  padding: 0 12px;
`;

const StyledPageSizeActions = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
`;

const StyledPageSelector = styled.div`
  display: grid;
  grid-auto-flow: column;
`;

const StyledRowPerPage = styled.span`
  margin-right: 8px;
`;

const StyledNumberRows = styled.span`
  margin-left: 8px;
`;

/*
 * Component.
 */

export const PaginatedTableFooter = forwardRef(
  (props: PaginatedTableFooterProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {total, pageSize, pageSizes, setPageSize, pageIndex, setPageIndex} = props;

    const pageCount = Math.ceil(total / pageSize);
    const canPreviousPage = pageIndex > 0;
    const canNextPage = pageIndex < pageCount - 1;

    const onPageSizeSelect = (size: number) => {
      setPageIndex((prevIndex) => Math.floor((prevIndex * pageSize) / size));
      setPageSize(size);
    };

    return (
      <StyledPaginatedTableFooter ref={ref}>
        <StyledPageSizeActions>
          <StyledRowPerPage>Rows per page</StyledRowPerPage>
          <TablePageSizeDropdownBox
            selectedPageSize={pageSize}
            onPageSizeSelect={onPageSizeSelect}
            pageSizes={pageSizes}
          />
          <StyledNumberRows>
            <Trans id="paginated-table-footer-number-rows">
              {`${pageIndex * pageSize + 1} - ${Math.min(total, (pageIndex + 1) * pageSize)} of ${total}`}
            </Trans>
          </StyledNumberRows>
        </StyledPageSizeActions>
        <StyledPageSelector>
          <StyledPageSelectorButton
            onClick={() => canPreviousPage && setPageIndex(0)}
            $disabled={!canPreviousPage}
          >
            <Icon name="firstPageSmall" />
          </StyledPageSelectorButton>
          <StyledPageSelectorButton
            onClick={() => canPreviousPage && setPageIndex((prevState) => prevState - 1)}
            $disabled={!canPreviousPage}
          >
            <Icon name="arrowBack" />
          </StyledPageSelectorButton>
          {renderPageNumbers(pageIndex + 1, pageCount, (index) => setPageIndex(index))}
          <StyledPageSelectorButton
            onClick={() => canNextPage && setPageIndex((prevState) => prevState + 1)}
            $disabled={!canNextPage}
          >
            <Icon name="arrowNext" />
          </StyledPageSelectorButton>
          <StyledPageSelectorButton
            onClick={() => canPreviousPage && setPageIndex(pageCount - 1)}
            $disabled={!canPreviousPage}
          >
            <Icon name="lastPageSmall" />
          </StyledPageSelectorButton>
        </StyledPageSelector>
      </StyledPaginatedTableFooter>
    );
  },
);
